/**
 * IndexedList - 頭文字セクション + インデックスレール付きの仮想化リスト
 *
 * 曲 / アーティスト / アルバムなど、名前順に大量の項目を並べる画面で共通利用する。
 */

import { useVirtualizer } from '@tanstack/react-virtual'
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'

import { IndexRail } from './index-rail'
import { compareIndexKey, getIndexKey } from './kana-index'

const SECTION_HEADER_HEIGHT = 36

type ListRow<T> = { type: 'header'; key: string; indexKey: string } | { type: 'item'; key: string; item: T }

type IndexedListProps<T> = {
  items: T[]
  /** 行の一意キー */
  getKey: (item: T) => string
  /** 並び順とインデックス判定に使う名前 */
  getName: (item: T) => string
  /** 1項目あたりの高さ(px) */
  rowHeight: number
  renderRow: (item: T) => ReactNode
  /** 名前順の代わりに使う比較関数 */
  compare?: (a: T, b: T) => number
  /** compare 指定時のセクション見出し（未指定ならセクションとインデックスレールを出さない） */
  getSectionKey?: (item: T) => string
  /** 降順にするか */
  descending?: boolean
}

export const IndexedList = <T,>({
  items,
  getKey,
  getName,
  rowHeight,
  renderRow,
  compare,
  getSectionKey,
  descending = false,
}: IndexedListProps<T>) => {
  'use no memo' // TanStack Virtual は React Compiler と非互換なため memoization をスキップ

  const scrollRef = useRef<HTMLDivElement>(null)

  const rows = useMemo(() => {
    /** ソート済みの並びをセクション見出し付きの行リストへ変換する */
    const toRows = (sorted: T[], getIndex: ((item: T) => string) | undefined): ListRow<T>[] => {
      if (!getIndex) return sorted.map((item): ListRow<T> => ({ type: 'item', key: getKey(item), item }))

      const result: ListRow<T>[] = []
      let currentKey: string | null = null
      for (const item of sorted) {
        const indexKey = getIndex(item)
        if (indexKey !== currentKey) {
          currentKey = indexKey
          result.push({ type: 'header', key: `header-${indexKey}`, indexKey })
        }
        result.push({ type: 'item', key: getKey(item), item })
      }
      return result
    }

    if (compare) {
      const sorted = [...items].sort(compare)
      if (descending) sorted.reverse()
      return toRows(sorted, getSectionKey)
    }

    const collator = new Intl.Collator('ja')
    const sorted = [...items].sort((a, b) => {
      const keyDiff = compareIndexKey(getIndexKey(getName(a)), getIndexKey(getName(b)))
      if (keyDiff !== 0) return keyDiff
      return collator.compare(getName(a), getName(b))
    })
    if (descending) sorted.reverse()

    return toRows(sorted, (item) => getIndexKey(getName(item)))
  }, [items, getKey, getName, compare, getSectionKey, descending])

  /** インデックスキー → その見出し行の位置 */
  const indexKeyToRow = useMemo(() => {
    const map = new Map<string, number>()
    rows.forEach((row, i) => {
      if (row.type === 'header') map.set(row.indexKey, i)
    })
    return map
  }, [rows])

  const estimateSize = useCallback(
    (index: number) => (rows.at(index)?.type === 'header' ? SECTION_HEADER_HEIGHT : rowHeight),
    [rows, rowHeight],
  )

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual は React Compiler 不対応、上記 'use no memo' で memoize 除外済み
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize,
    overscan: 12,
  })

  useEffect(() => {
    virtualizer.measure()
  }, [virtualizer, rows])

  const handleIndexJump = useCallback(
    (indexKey: string) => {
      const rowIndex = indexKeyToRow.get(indexKey)
      if (rowIndex === undefined) return
      virtualizer.scrollToIndex(rowIndex, { align: 'start' })
    },
    [indexKeyToRow, virtualizer],
  )

  const indexKeys = useMemo(() => [...indexKeyToRow.keys()], [indexKeyToRow])

  return (
    <div className="relative min-h-0 flex-1">
      <div ref={scrollRef} className="h-full overflow-y-auto pr-6">
        <div className="relative" style={{ height: virtualizer.getTotalSize() }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows.at(virtualRow.index)
            if (!row) return null
            return (
              <div
                key={row.key}
                className="absolute inset-x-0 top-0"
                style={{ height: virtualRow.size, transform: `translateY(${virtualRow.start}px)` }}
              >
                {row.type === 'header' ? (
                  <div className="flex h-full items-end px-4 pb-1 text-xl font-bold">{row.indexKey}</div>
                ) : (
                  renderRow(row.item)
                )}
              </div>
            )
          })}
        </div>
      </div>

      <IndexRail indexKeys={indexKeys} onJump={handleIndexJump} />
    </div>
  )
}
